/**
 * src/features/referral/ui/InviteUrlRow.tsx
 *
 * Displays the full invite URL with a Copy button and 2s confirmation state.
 * T-27-04: URL rendered as React text content (auto-escaped) — no dangerouslySetInnerHTML.
 * T-27-06: navigator.clipboard wrapped in try/catch with execCommand fallback.
 */

import { FC, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { Skeleton } from '@/shared/ui/Skeleton';

/** Duration in ms for which the copy confirmation state is shown. */
const COPY_CONFIRM_MS = 2000;

interface InviteUrlRowProps {
  url: string | null;
  loading: boolean;
}

export const InviteUrlRow: FC<InviteUrlRowProps> = ({ url, loading }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [copied, setCopied] = useState(false);
  // Ref holds the pending reset timer so we can cancel it on re-click or unmount.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending timer on unmount to prevent setState after unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!url) return;
    let success = false;
    try {
      await navigator.clipboard.writeText(url);
      success = true;
    } catch {
      // Fallback: select text in the URL display element and use execCommand
      const el = document.getElementById('invite-url-display');
      if (el instanceof HTMLInputElement) {
        el.select();
        try {
          // legacy fallback — intentional
          document.execCommand('copy');
          success = true;
        } catch {
          // execCommand also unavailable; leave success=false
        }
      }
    }
    if (!success) return;
    // Cancel any previous pending reset before starting a new one.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    setCopied(true);
    timerRef.current = setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, COPY_CONFIRM_MS);
  }, [url]);

  if (loading) {
    return (
      <div
        className="flex gap-2"
        aria-label={t('referral.loading', 'Loading your referral link...')}
        aria-busy="true"
      >
        <Skeleton className="h-9 flex-1 rounded-lg" />
        <Skeleton className="h-9 w-24 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {t('referral.invite_url_label', 'Your invite link')}
      </span>
      <div className="flex items-center gap-2">
        <input
          id="invite-url-display"
          type="text"
          readOnly
          value={url ?? ''}
          aria-label={t('referral.invite_url_label', 'Your invite link')}
          className="flex-1 text-body bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-700 dark:text-slate-300 select-all focus:outline-none focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-400 cursor-text"
        />
        <button
          onClick={handleCopy}
          disabled={!url}
          aria-pressed={copied}
          aria-live="polite"
          className="min-h-[44px] px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-body transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <AnimatePresence mode="wait">
            {copied ? (
              <motion.span
                key="copied"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: reduced ? 0 : 0.15 }}
                className="flex items-center gap-1"
              >
                <Check size={16} />
                {t('referral.copied', 'Copied!')}
              </motion.span>
            ) : (
              <motion.span
                key="copy"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: reduced ? 0 : 0.15 }}
                className="flex items-center gap-1"
              >
                <Copy size={16} />
                {t('referral.copy', 'Copy link')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </div>
  );
};
